<template>
  <div class="accounts-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Page Header: Breadcrumbs, Title, Subtitle & Action Buttons -->
    <div class="page-header-wrap mb-4">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <!-- Left: Breadcrumb + Title + Subtitle -->
        <div class="header-text-block">
          <PageBreadcrumb :items="[{ text: 'Accounts & Vouchers' }]" class="mb-1" />
          <p class="page-subtitle mb-0">Create and track cash receipts, vendor payments, and double-entry journal vouchers.</p>
        </div>

        <!-- Right: Header Actions -->
        <div class="header-actions-group d-flex flex-wrap align-center mt-3 mt-sm-0">
          <v-btn
            outlined
            depressed
            class="btn-action-outline mr-2"
            @click="$router.push('/AccountingVouchersReport')"
          >
            <v-icon left size="16" class="btn-icon">mdi-printer-outline</v-icon>
            Print Entries
          </v-btn>

          <v-btn
            depressed
            class="btn-primary-gold mr-2"
            @click="$router.push('/AccountHeads')"
          >
            <v-icon left size="16">mdi-format-list-bulleted</v-icon>
            Account Heads
          </v-btn>

          <v-btn
            outlined
            depressed
            class="btn-action-outline btn-icon-only"
            @click="onTabChange()"
            title="Refresh Entries"
          >
            <v-icon size="17" color="#64748b">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Segmented Voucher Type Navigation Tabs -->
    <div class="voucher-nav-bar mb-4">
      <div class="voucher-segmented-control">
        <button
          type="button"
          class="voucher-tab-btn"
          :class="{ 'is-active is-receipt': tab === 0 }"
          @click="setTab(0)"
        >
          <div class="tab-icon-wrap">
            <v-icon size="15">mdi-arrow-down-left-bold</v-icon>
          </div>
          <span class="tab-label">Receipt</span>
          <span class="tab-count-pill" v-if="tab === 0 && entryList">
            {{ entryList.length }}
          </span>
        </button>

        <button
          type="button"
          class="voucher-tab-btn"
          :class="{ 'is-active is-payment': tab === 1 }"
          @click="setTab(1)"
        >
          <div class="tab-icon-wrap">
            <v-icon size="15">mdi-arrow-up-right-bold</v-icon>
          </div>
          <span class="tab-label">Payment</span>
          <span class="tab-count-pill" v-if="tab === 1 && entryList">
            {{ entryList.length }}
          </span>
        </button>

        <button
          type="button"
          class="voucher-tab-btn"
          :class="{ 'is-active is-journal': tab === 2 }"
          @click="setTab(2)"
        >
          <div class="tab-icon-wrap">
            <v-icon size="15">mdi-swap-horizontal-bold</v-icon>
          </div>
          <span class="tab-label">Journal</span>
          <span class="tab-count-pill" v-if="tab === 2 && entryList">
            {{ entryList.length }}
          </span>
        </button>
      </div>
    </div>

    <!-- 1. RECEIPT / PAYMENT FORM CARD -->
    <v-card class="form-card rounded-xl mb-6" elevation="0" v-if="voucherType !== 'Journal'">
      <!-- Card Header -->
      <div class="form-card-header d-flex align-center justify-space-between px-5 py-3 border-bottom">
        <div class="d-flex align-center">
          <div class="header-icon-pill mr-3" :class="voucherType === 'Receipt' ? 'icon-pill-receipt' : 'icon-pill-payment'">
            <v-icon size="18" :color="voucherType === 'Receipt' ? '#059669' : '#d97706'">
              {{ voucherType === 'Receipt' ? 'mdi-arrow-down-left-bold' : 'mdi-arrow-up-right-bold' }}
            </v-icon>
          </div>
          <div>
            <h2 class="section-title mb-0">New {{ voucherType }} Voucher</h2>
            <p class="section-sub mb-0">
              {{ voucherType === 'Receipt'
                ? 'Record incoming funds from customers, debtors, or revenue ledger heads.'
                : 'Record outgoing disbursement to vendors, karigars, or expense ledger heads.'
              }}
            </p>
          </div>
        </div>

        <v-btn
          text
          x-small
          class="btn-reset-form"
          @click="resetForm"
          title="Reset input fields"
        >
          <v-icon left size="13">mdi-refresh</v-icon>
          Reset
        </v-btn>
      </div>

      <!-- Card Form Body -->
      <div class="form-card-body px-5 py-4">
        <v-row>
          <!-- Voucher No. -->
          <v-col cols="12" sm="6" md="3" class="py-2">
            <label class="form-field-label">
              VOUCHER NO. <span class="badge-auto">Auto</span>
            </label>
            <v-text-field
              v-model="voucherNo"
              outlined
              dense
              disabled
              hide-details
              class="modern-input-field readonly-field"
              prepend-inner-icon="mdi-pound"
            ></v-text-field>
          </v-col>

          <!-- Date -->
          <v-col cols="12" sm="6" md="3" class="py-2">
            <label class="form-field-label">
              DATE <span class="required-asterisk">*</span>
            </label>
            <v-menu
              ref="dateMenu"
              v-model="dateMenu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="date"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  class="modern-input-field cursor-pointer"
                  prepend-inner-icon="mdi-calendar-range"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="date"
                no-title
                scrollable
                color="#9a6b27"
                @change="$refs.dateMenu.save(date)"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Cash / Bank Head -->
          <v-col cols="12" sm="6" md="3" class="py-2">
            <label class="form-field-label">
              CASH / BANK HEAD <span class="required-asterisk">*</span>
            </label>
            <v-select
              v-model="cashOrBankHeadId"
              :items="cashBankHeads"
              item-text="name"
              item-value="_id"
              outlined
              dense
              hide-details
              placeholder="Select Cash / Bank"
              class="modern-input-field modern-select"
              color="#9a6b27"
              prepend-inner-icon="mdi-bank-outline"
            ></v-select>
          </v-col>

          <!-- Account Head -->
          <v-col cols="12" sm="6" md="3" class="py-2">
            <label class="form-field-label">
              {{ voucherType === "Receipt" ? "RECEIVED FROM (HEAD)" : "PAID TO (HEAD)" }} <span class="required-asterisk">*</span>
            </label>
            <v-select
              v-model="accountHeadId"
              :items="otherHeads"
              item-text="name"
              item-value="_id"
              outlined
              dense
              hide-details
              :placeholder="voucherType === 'Receipt' ? 'Select Payer Head' : 'Select Payee Head'"
              class="modern-input-field modern-select"
              color="#9a6b27"
              prepend-inner-icon="mdi-account-cash-outline"
            ></v-select>
          </v-col>

          <!-- Amount -->
          <v-col cols="12" sm="6" md="3" class="py-2">
            <label class="form-field-label">
              AMOUNT (₹) <span class="required-asterisk">*</span>
            </label>
            <v-text-field
              v-model="amount"
              type="number"
              step="any"
              min="0"
              outlined
              dense
              hide-details
              placeholder="0.00"
              class="modern-input-field font-amount-input"
              color="#9a6b27"
              prefix="₹"
              @keydown.enter="saveReceiptOrPayment"
            ></v-text-field>
          </v-col>

          <!-- Narration -->
          <v-col cols="12" sm="6" md="6" class="py-2">
            <label class="form-field-label">
              NARRATION / REMARKS <span class="optional-hint">(Optional)</span>
            </label>
            <v-text-field
              v-model="narration"
              outlined
              dense
              hide-details
              placeholder="e.g. Estimate cash sale, Shop maintenance, Staff advance"
              class="modern-input-field"
              color="#9a6b27"
              prepend-inner-icon="mdi-text-box-outline"
              @keydown.enter="saveReceiptOrPayment"
            ></v-text-field>
          </v-col>

          <!-- Submit Button -->
          <v-col cols="12" sm="12" md="3" class="py-2 d-flex align-end">
            <v-btn
              depressed
              class="btn-submit-voucher w-100"
              @click="saveReceiptOrPayment()"
              :loading="appLoading"
            >
              <v-icon left size="16">mdi-check-circle-outline</v-icon>
              Save {{ voucherType }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <!-- 2. JOURNAL FORM CARD -->
    <v-card class="form-card rounded-xl mb-6" elevation="0" v-if="voucherType === 'Journal'">
      <!-- Header -->
      <div class="form-card-header d-flex align-center justify-space-between px-5 py-3 border-bottom">
        <div class="d-flex align-center">
          <div class="header-icon-pill icon-pill-journal mr-3">
            <v-icon size="18" color="#7c3aed">mdi-swap-horizontal-bold</v-icon>
          </div>
          <div>
            <h2 class="section-title mb-0">New Journal Entry</h2>
            <p class="section-sub mb-0">Record multi-line debits and credits between ledger heads (must be balanced).</p>
          </div>
        </div>

        <v-btn
          text
          x-small
          class="btn-reset-form"
          @click="resetForm"
        >
          <v-icon left size="13">mdi-refresh</v-icon>
          Reset
        </v-btn>
      </div>

      <!-- Journal Body -->
      <div class="form-card-body px-5 py-4">
        <!-- Top Row: Voucher No, Date, Narration -->
        <v-row class="mb-2">
          <v-col cols="12" sm="6" md="3" class="py-2">
            <label class="form-field-label">
              VOUCHER NO. <span class="badge-auto">Auto</span>
            </label>
            <v-text-field
              v-model="voucherNo"
              outlined
              dense
              disabled
              hide-details
              class="modern-input-field readonly-field"
              prepend-inner-icon="mdi-pound"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="3" class="py-2">
            <label class="form-field-label">
              DATE <span class="required-asterisk">*</span>
            </label>
            <v-menu
              ref="jDateMenu"
              v-model="dateMenu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="date"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  class="modern-input-field cursor-pointer"
                  prepend-inner-icon="mdi-calendar-range"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="date"
                no-title
                scrollable
                color="#9a6b27"
                @change="$refs.jDateMenu.save(date)"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="12" md="6" class="py-2">
            <label class="form-field-label">
              NARRATION / REMARKS <span class="optional-hint">(Optional)</span>
            </label>
            <v-text-field
              v-model="narration"
              outlined
              dense
              hide-details
              placeholder="e.g. Month-end adjustment, depreciation, inter-account transfer"
              class="modern-input-field"
              color="#9a6b27"
              prepend-inner-icon="mdi-text-box-outline"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Journal Lines Table -->
        <div class="journal-lines-container rounded-lg p-3 mb-4">
          <div class="journal-lines-header d-none d-sm-flex align-center px-3 py-2">
            <div class="col-jl-head flex-grow-1">ACCOUNT HEAD</div>
            <div class="col-jl-debit">DEBIT (₹)</div>
            <div class="col-jl-credit">CREDIT (₹)</div>
            <div class="col-jl-del text-right">ACTION</div>
          </div>

          <div
            v-for="(line, i) in journalLines"
            :key="i"
            class="journal-line-row d-flex flex-wrap align-center px-3 py-2 mb-2 rounded-lg"
          >
            <!-- Account Head -->
            <div class="col-jl-head flex-grow-1 pr-sm-2 mb-2 mb-sm-0 w-100-mobile">
              <span class="d-sm-none form-field-label">Account Head</span>
              <v-select
                v-model="line.accountHeadId"
                :items="allHeads"
                item-text="name"
                item-value="_id"
                outlined
                dense
                hide-details
                placeholder="Select Account Head"
                class="modern-input-field modern-select"
                color="#9a6b27"
              ></v-select>
            </div>

            <!-- Debit -->
            <div class="col-jl-debit pr-sm-2 mb-2 mb-sm-0 w-50-mobile">
              <span class="d-sm-none form-field-label">Debit (₹)</span>
              <v-text-field
                v-model="line.debitAmount"
                type="number"
                step="any"
                min="0"
                outlined
                dense
                hide-details
                placeholder="Debit"
                class="modern-input-field"
                color="#9a6b27"
              ></v-text-field>
            </div>

            <!-- Credit -->
            <div class="col-jl-credit pr-sm-2 mb-2 mb-sm-0 w-50-mobile">
              <span class="d-sm-none form-field-label">Credit (₹)</span>
              <v-text-field
                v-model="line.creditAmount"
                type="number"
                step="any"
                min="0"
                outlined
                dense
                hide-details
                placeholder="Credit"
                class="modern-input-field"
                color="#9a6b27"
              ></v-text-field>
            </div>

            <!-- Delete Line -->
            <div class="col-jl-del text-right">
              <v-btn
                icon
                small
                class="btn-delete-line"
                @click="removeJournalLine(i)"
                title="Remove line"
                :disabled="journalLines.length <= 2"
              >
                <v-icon size="16" color="#ef4444">mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Add Line Button -->
          <div class="mt-2 px-1">
            <v-btn
              outlined
              small
              class="btn-add-line"
              @click="journalLines.push({ accountHeadId: null, debitAmount: 0, creditAmount: 0 })"
            >
              <v-icon left size="14">mdi-plus</v-icon>
              Add Journal Line
            </v-btn>
          </div>
        </div>

        <!-- Journal Balance Status Bar & Submit -->
        <div class="journal-footer-wrap d-flex flex-wrap align-center justify-space-between p-3 rounded-lg">
          <div class="journal-balance-summary d-flex flex-wrap align-center gap-3">
            <div class="summary-metric">
              <span class="metric-label">TOTAL DEBIT:</span>
              <span class="metric-val text-debit">₹ {{ formatCurrency(journalTotalDebit) }}</span>
            </div>
            <div class="summary-metric">
              <span class="metric-label">TOTAL CREDIT:</span>
              <span class="metric-val text-credit">₹ {{ formatCurrency(journalTotalCredit) }}</span>
            </div>
            <div class="balance-status-badge">
              <span v-if="isJournalBalanced" class="badge-balanced">
                <v-icon size="13" color="#059669" class="mr-1">mdi-check-circle</v-icon>
                Balanced
              </span>
              <span v-else class="badge-unbalanced">
                <v-icon size="13" color="#e11d48" class="mr-1">mdi-alert-circle</v-icon>
                Diff: ₹ {{ formatCurrency(journalDifference) }} (Not Balanced)
              </span>
            </div>
          </div>

          <v-btn
            depressed
            class="btn-submit-voucher mt-2 mt-sm-0"
            @click="saveJournal()"
            :loading="appLoading"
          >
            <v-icon left size="16">mdi-check-circle-outline</v-icon>
            Save Journal
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 3. VOUCHERS LIST TABLE CARD -->
    <v-card class="table-card rounded-xl" elevation="0">
      <!-- Card Top Bar with Search & Total Amount -->
      <div class="table-card-top-bar d-flex flex-wrap align-center justify-space-between px-5 py-3 border-bottom">
        <div class="d-flex flex-wrap align-center gap-2 my-1">
          <h2 class="section-title mb-0">{{ voucherType }} Register</h2>
          <span class="active-count-pill" v-if="entryList">
            {{ filteredEntryList.length }} {{ filteredEntryList.length === 1 ? 'voucher' : 'vouchers' }}
          </span>

          <span class="total-volume-pill ml-sm-2" v-if="filteredEntryList.length > 0">
            Total: <strong>₹ {{ formatCurrency(totalVoucherAmount) }}</strong>
          </span>
        </div>

        <div class="d-flex align-center my-1">
          <!-- Real-Time Search Input -->
          <v-text-field
            v-model="searchQuery"
            outlined
            dense
            hide-details
            clearable
            placeholder="Search voucher no., head, remarks..."
            class="filter-search-input"
            color="#9a6b27"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>
      </div>

      <!-- Table Body -->
      <div class="table-responsive" v-if="filteredEntryList.length > 0">
        <table class="custom-table">
          <thead>
            <tr>
              <th class="col-sno text-center">#</th>
              <th class="col-voucher">VOUCHER NO.</th>
              <th class="col-date">DATE</th>
              <th class="col-head">CASH / BANK</th>
              <th class="col-narration">NARRATION</th>
              <th class="col-amount text-right">AMOUNT</th>
              <th class="col-action text-right">ACTION</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(value, i) in filteredEntryList"
              :key="value._id || i"
              class="table-row"
            >
              <!-- S.No -->
              <td class="col-sno text-center text-muted font-mono">
                {{ i + 1 }}
              </td>

              <!-- Voucher No -->
              <td class="col-voucher">
                <span class="voucher-no-badge font-mono">{{ value.voucherNo }}</span>
              </td>

              <!-- Date -->
              <td class="col-date text-slate-700">
                <div class="d-flex align-center">
                  <v-icon size="14" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
                  <span>{{ formatDate(value.date) }}</span>
                </div>
              </td>

              <!-- Cash / Bank -->
              <td class="col-head">
                <div class="d-flex align-center" v-if="value.cashOrBankHeadId">
                  <div class="head-icon-mini mr-2">
                    <v-icon size="13" color="#047857">mdi-bank-outline</v-icon>
                  </div>
                  <span class="head-name-text">{{ value.cashOrBankHeadId.name }}</span>
                </div>
                <span v-else class="text-muted-dash">—</span>
              </td>

              <!-- Narration -->
              <td class="col-narration text-slate-600">
                <span v-if="value.narration" class="narration-text" :title="value.narration">{{ value.narration }}</span>
                <span v-else class="text-muted-dash">—</span>
              </td>

              <!-- Amount -->
              <td class="col-amount text-right">
                <span class="voucher-amount-val font-mono" :class="getAmountColorClass()">
                  {{ getAmountPrefix() }} ₹ {{ formatCurrency(value.totalAmount || 0) }}
                </span>
              </td>

              <!-- Action -->
              <td class="col-action text-right">
                <v-btn
                  icon
                  small
                  class="btn-delete-action"
                  @click="deleteEntry(value._id, value.voucherNo)"
                  title="Delete voucher"
                >
                  <v-icon size="17" color="#ef4444">mdi-delete-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center py-10 px-4">
        <div class="empty-icon-circle mx-auto mb-3">
          <v-icon size="36" color="#94a3b8">mdi-book-open-blank-variant-outline</v-icon>
        </div>
        <h3 class="empty-title mb-1">
          {{ searchQuery ? 'No matching vouchers found' : 'No ' + voucherType.toLowerCase() + ' vouchers recorded' }}
        </h3>
        <p class="empty-desc mb-4">
          {{ searchQuery
            ? 'Try clearing your search keyword to see all entries.'
            : 'Fill in the form above to record your first ' + voucherType.toLowerCase() + ' voucher.'
          }}
        </p>
        <v-btn
          v-if="searchQuery"
          outlined
          small
          class="btn-action-outline"
          @click="searchQuery = ''"
        >
          <v-icon left size="14">mdi-close</v-icon>
          Clear Search
        </v-btn>
      </div>

      <!-- Table Card Footer -->
      <div class="table-card-footer d-flex align-center justify-space-between px-5 py-3 border-top" v-if="filteredEntryList.length > 0">
        <div class="footer-summary-text">
          Showing <strong>{{ filteredEntryList.length }}</strong> of <strong>{{ entryList.length }}</strong> {{ voucherType.toLowerCase() }} entries
        </div>
        <div class="footer-total-summary">
          Total {{ voucherType }}: <strong class="ml-1" :class="getAmountColorClass()">₹ {{ formatCurrency(totalVoucherAmount) }}</strong>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      tab: 0,
      dateMenu: false,
      voucherNo: "",
      checkVoucherNo: null,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      narration: "",
      cashOrBankHeadId: null,
      accountHeadId: null,
      amount: "",
      searchQuery: "",
      journalLines: [
        { accountHeadId: null, debitAmount: 0, creditAmount: 0 },
        { accountHeadId: null, debitAmount: 0, creditAmount: 0 },
      ],
      allHeads: [],
      entryList: [],
    };
  },
  computed: {
    voucherType() {
      return ["Receipt", "Payment", "Journal"][this.tab];
    },
    cashBankHeads() {
      return this.allHeads.filter((h) => h.isCashOrBank);
    },
    otherHeads() {
      return this.allHeads.filter((h) => !h.isCashOrBank);
    },
    journalTotalDebit() {
      return this.journalLines.reduce(
        (sum, l) => sum + (parseFloat(l.debitAmount) || 0),
        0
      );
    },
    journalTotalCredit() {
      return this.journalLines.reduce(
        (sum, l) => sum + (parseFloat(l.creditAmount) || 0),
        0
      );
    },
    journalDifference() {
      return Math.abs(this.journalTotalDebit - this.journalTotalCredit);
    },
    isJournalBalanced() {
      return (
        this.journalDifference <= 0.01 &&
        this.journalTotalDebit > 0
      );
    },
    filteredEntryList() {
      if (!this.entryList) return [];
      if (!this.searchQuery || !this.searchQuery.trim()) return this.entryList;
      const q = this.searchQuery.toLowerCase().trim();
      return this.entryList.filter((item) => {
        const vNo = item.voucherNo && item.voucherNo.toLowerCase().includes(q);
        const narr = item.narration && item.narration.toLowerCase().includes(q);
        const head =
          item.cashOrBankHeadId &&
          item.cashOrBankHeadId.name &&
          item.cashOrBankHeadId.name.toLowerCase().includes(q);
        const amt = item.totalAmount && String(item.totalAmount).includes(q);
        return vNo || narr || head || amt;
      });
    },
    totalVoucherAmount() {
      if (!this.filteredEntryList) return 0;
      return this.filteredEntryList.reduce(
        (sum, item) => sum + (parseFloat(item.totalAmount) || 0),
        0
      );
    },
  },
  mounted() {
    this.getHeads();
    this.generateVoucherNo();
    this.getList();
  },
  methods: {
    setTab(index) {
      if (this.tab === index) return;
      this.tab = index;
      this.searchQuery = "";
      this.onTabChange();
    },
    onTabChange() {
      this.generateVoucherNo();
      this.getList();
    },
    formatCurrency(val) {
      if (val === null || val === undefined || val === "") return "0.00";
      const num = Number(val);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getAmountColorClass() {
      if (this.voucherType === "Receipt") return "color-receipt";
      if (this.voucherType === "Payment") return "color-payment";
      return "color-journal";
    },
    getAmountPrefix() {
      if (this.voucherType === "Receipt") return "+";
      if (this.voucherType === "Payment") return "-";
      return "";
    },
    removeJournalLine(index) {
      if (this.journalLines.length > 2) {
        this.journalLines.splice(index, 1);
      } else {
        this.msg = "Journal requires at least 2 lines";
        this.showsnackbar = true;
      }
    },
    getHeads() {
      axios({
        method: "get",
        url: "/accounthead/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.allHeads = response.data.data;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    generateVoucherNo() {
      axios({
        method: "get",
        url: "/accounting/bill/generate",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { voucherType: this.voucherType },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.voucherNo = response.data.voucherNo;
            this.checkVoucherNo = response.data.checkVoucherNo;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/accounting/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { voucherType: this.voucherType },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.entryList = response.data.data;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    resetForm() {
      this.narration = "";
      this.accountHeadId = null;
      this.cashOrBankHeadId = null;
      this.amount = "";
      this.journalLines = [
        { accountHeadId: null, debitAmount: 0, creditAmount: 0 },
        { accountHeadId: null, debitAmount: 0, creditAmount: 0 },
      ],
      this.generateVoucherNo();
    },
    saveReceiptOrPayment() {
      if (!this.cashOrBankHeadId) {
        this.msg = "Please select Cash / Bank account head";
        this.showsnackbar = true;
        return;
      }
      if (!this.accountHeadId) {
        this.msg = `Please select ${
          this.voucherType === "Receipt" ? "Received From" : "Paid To"
        } head`;
        this.showsnackbar = true;
        return;
      }
      if (!this.amount || parseFloat(this.amount) <= 0) {
        this.msg = "Please enter a valid amount";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "post",
        url: "/accounting/add",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: {
          voucherType: this.voucherType,
          voucherNo: this.voucherNo,
          checkVoucherNo: this.checkVoucherNo,
          date: this.date,
          narration: this.narration,
          accountHeadId: this.accountHeadId,
          cashOrBankHeadId: this.cashOrBankHeadId,
          amount: this.amount,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showsnackbar = true;
          if (response.data.status == true) {
            this.resetForm();
            this.getList();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    saveJournal() {
      if (Math.abs(this.journalTotalDebit - this.journalTotalCredit) > 0.01) {
        this.msg = "Debit and credit totals must match";
        this.showsnackbar = true;
        return;
      }
      if (this.journalLines.some((l) => !l.accountHeadId)) {
        this.msg = "Please select an account head for every line";
        this.showsnackbar = true;
        return;
      }
      if (this.journalTotalDebit <= 0) {
        this.msg = "Please enter journal amounts";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "post",
        url: "/accounting/add",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: {
          voucherType: "Journal",
          voucherNo: this.voucherNo,
          checkVoucherNo: this.checkVoucherNo,
          date: this.date,
          narration: this.narration,
          lines: this.journalLines,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showsnackbar = true;
          if (response.data.status == true) {
            this.resetForm();
            this.getList();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    async deleteEntry(id, voucherNo) {
      const label = voucherNo
        ? ` voucher "${voucherNo}"`
        : ` this ${this.voucherType.toLowerCase()} entry`;
      if (this.$confirm) {
        const ok = await this.$confirm(
          `Are you sure you want to delete${label}? This action cannot be undone.`,
          {
            title: `Delete ${this.voucherType} Entry`,
            confirmText: "Delete",
          }
        );
        if (!ok) return;
      }
      this.appLoading = true;
      axios({
        method: "post",
        url: "/accounting/delete",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { id: id },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showsnackbar = true;
          if (response.data.status == true) {
            this.getList();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    formatDate(item) {
      if (!item) return "-";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return item;
      const day = dt.getDate();
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[dt.getMonth()];
      const year = dt.getFullYear();
      return `${day} ${month} ${year}`;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@400;500;600;700&display=swap");

.accounts-page {
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #1e293b;
  padding-bottom: 32px;
}

/* Page Header */
.page-header-wrap {
  padding: 2px 0;
}

.breadcrumbs-row {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.breadcrumb-sep {
  color: #94a3b8;
  margin: 0 6px;
  font-weight: 300;
}

.breadcrumb-item.active {
  color: #9a6b27;
  font-weight: 600;
}

.page-main-title {
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 25px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
  margin: 0;
  letter-spacing: -0.025em;
}

.page-subtitle {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13.5px;
  color: #64748b;
  font-weight: 400;
  margin-top: 3px;
  letter-spacing: -0.01em;
}

/* Header Buttons */
.btn-action-outline {
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
  letter-spacing: -0.01em !important;
  transition: all 0.2s ease !important;
}

.btn-action-outline .btn-icon {
  color: #64748b !important;
  transition: color 0.2s ease;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
  background-color: #fdfaf6 !important;
  transform: translateY(-1px);
}

.btn-action-outline:hover .btn-icon {
  color: #9a6b27 !important;
}

.btn-primary-gold {
  height: 38px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-primary-gold:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1px);
}

.btn-icon-only {
  min-width: 38px !important;
  width: 38px !important;
  padding: 0 !important;
}

/* Voucher Segmented Control */
.voucher-nav-bar {
  display: flex;
}

.voucher-segmented-control {
  display: inline-flex;
  background-color: #f1ede7;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid #e5dfd5;
}

.voucher-tab-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}

.voucher-tab-btn:hover:not(.is-active) {
  color: #1e293b;
  background-color: rgba(255, 255, 255, 0.5);
}

.tab-icon-wrap {
  display: flex;
  align-items: center;
  margin-right: 7px;
}

.tab-icon-wrap .v-icon {
  color: inherit;
}

.voucher-tab-btn.is-active {
  background-color: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.voucher-tab-btn.is-active.is-receipt {
  color: #059669;
}

.voucher-tab-btn.is-active.is-payment {
  color: #d97706;
}

.voucher-tab-btn.is-active.is-journal {
  color: #7c3aed;
}

.tab-count-pill {
  font-size: 11px;
  font-weight: 700;
  background-color: #f1f5f9;
  color: #64748b;
  padding: 1px 7px;
  border-radius: 999px;
  margin-left: 7px;
}

.voucher-tab-btn.is-active.is-receipt .tab-count-pill {
  background-color: #ecfdf5;
  color: #059669;
}

.voucher-tab-btn.is-active.is-payment .tab-count-pill {
  background-color: #fffbeb;
  color: #d97706;
}

.voucher-tab-btn.is-active.is-journal .tab-count-pill {
  background-color: #f5f3ff;
  color: #7c3aed;
}

/* Cards shared */
.form-card,
.table-card {
  background-color: #ffffff;
  border: 1px solid #e8e4dc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

/* Form Card Header */
.form-card-header {
  background: #fafaf9;
}

.header-icon-pill {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-pill-receipt {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.icon-pill-payment {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.icon-pill-journal {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
}

.section-title {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.section-sub {
  font-size: 12px;
  color: #64748b;
}

.btn-reset-form {
  font-size: 11.5px !important;
  color: #64748b !important;
  text-transform: none !important;
}

.btn-reset-form:hover {
  color: #9a6b27 !important;
}

/* Form Inputs */
.form-field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.badge-auto {
  font-size: 10px;
  font-weight: 600;
  color: #9a6b27;
  background-color: #fbf5ec;
  border: 1px solid #ebd9be;
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: 4px;
}

.required-asterisk {
  color: #ef4444;
}

.optional-hint {
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 400;
  text-transform: none;
}

.modern-input-field >>> .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
  font-size: 13px !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  background-color: #ffffff !important;
  border-color: #e2e8f0 !important;
}

.modern-input-field >>> input {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  color: #0f172a !important;
  letter-spacing: -0.01em !important;
}

.readonly-field >>> .v-input__slot {
  background-color: #f8fafc !important;
}

.cursor-pointer >>> input {
  cursor: pointer !important;
}

.modern-input-field >>> fieldset {
  border-color: #e2e8f0 !important;
}

.modern-input-field:hover >>> fieldset {
  border-color: #cbd5e1 !important;
}

.modern-input-field.v-input--is-focused >>> fieldset {
  border-color: #9a6b27 !important;
}

.modern-input-field >>> .v-input__prepend-inner {
  margin-top: 8px !important;
  margin-right: 6px !important;
}

.modern-input-field >>> .v-icon {
  font-size: 17px !important;
  color: #94a3b8 !important;
}

.modern-select >>> .v-select__selection {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  color: #0f172a !important;
  font-weight: 500 !important;
  letter-spacing: -0.01em !important;
}

.font-amount-input >>> input {
  font-weight: 700 !important;
  font-size: 14.5px !important;
  color: #0f172a !important;
}

.btn-submit-voucher {
  height: 40px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 9px !important;
  padding: 0 18px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-submit-voucher:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1px);
}

.w-100 {
  width: 100% !important;
}

/* Journal Lines Specific */
.journal-lines-container {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.journal-lines-header {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
}

.col-jl-head {
  min-width: 220px;
}

.col-jl-debit,
.col-jl-credit {
  width: 150px;
}

.col-jl-del {
  width: 44px;
}

.journal-line-row {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
}

.btn-delete-line {
  transition: all 0.15s ease;
}

.btn-delete-line:hover {
  background-color: #fef2f2 !important;
}

.btn-add-line {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #9a6b27 !important;
  border-color: #ebd9be !important;
  background-color: #ffffff !important;
  text-transform: none !important;
}

.btn-add-line:hover {
  background-color: #fdf8ee !important;
  border-color: #9a6b27 !important;
}

.journal-footer-wrap {
  background-color: #fafaf9;
  border: 1px solid #e8e4dc;
}

.summary-metric {
  font-size: 12.5px;
  display: inline-flex;
  align-items: center;
}

.metric-label {
  font-weight: 600;
  color: #64748b;
  margin-right: 4px;
}

.metric-val {
  font-weight: 700;
  font-family: monospace;
}

.text-debit {
  color: #059669;
}

.text-credit {
  color: #2563eb;
}

.badge-balanced {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #059669;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 3px 8px;
  border-radius: 6px;
}

.badge-unbalanced {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #e11d48;
  background-color: #fff1f2;
  border: 1px solid #fecdd3;
  padding: 3px 8px;
  border-radius: 6px;
}

/* Table Card Top Bar */
.table-card-top-bar {
  background-color: #ffffff;
}

.active-count-pill {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
}

.total-volume-pill {
  font-size: 12px;
  font-weight: 600;
  color: #85591c;
  background-color: #fcf6eb;
  border: 1px solid #ebd9be;
  padding: 3px 10px;
  border-radius: 6px;
}

.filter-search-input {
  width: 260px;
}

.filter-search-input >>> .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  font-size: 12.5px !important;
  border-radius: 8px !important;
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
}

.filter-search-input >>> input {
  font-size: 12.5px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}

/* Custom Table */
.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #fafaf9;
}

.custom-table th {
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #f1f5f9;
  user-select: none;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
}

.custom-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
}

.table-row {
  transition: background-color 0.12s ease;
}

.table-row:hover {
  background-color: #fafaf9;
}

/* Column Widths */
.col-sno {
  width: 48px;
  min-width: 44px;
}

.col-voucher {
  width: 140px;
}

.col-date {
  width: 150px;
}

.col-head {
  min-width: 180px;
}

.col-narration {
  min-width: 220px;
}

.col-amount {
  width: 150px;
}

.col-action {
  width: 90px;
}

.font-mono {
  font-variant-numeric: tabular-nums;
}

.text-muted {
  color: #94a3b8 !important;
  font-size: 12px;
}

.text-muted-dash {
  color: #cbd5e1;
  font-size: 14px;
}

.voucher-no-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.02em;
}

.head-icon-mini {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background-color: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.head-name-text {
  font-weight: 600;
  color: #0f172a;
}

.narration-text {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Amount Colors */
.voucher-amount-val {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.color-receipt {
  color: #059669;
}

.color-payment {
  color: #d97706;
}

.color-journal {
  color: #475569;
}

/* Delete Button */
.btn-delete-action {
  transition: all 0.18s ease;
  border-radius: 8px;
}

.btn-delete-action:hover {
  background-color: #fef2f2 !important;
  transform: scale(1.08);
}

/* Empty State */
.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  font-size: 13px;
  color: #64748b;
}

/* Table Card Footer */
.table-card-footer {
  background-color: #fafaf9;
}

.footer-summary-text {
  font-size: 12px;
  color: #64748b;
}

.footer-total-summary {
  font-size: 12.5px;
  color: #475569;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

@media (max-width: 600px) {
  .w-100-mobile {
    width: 100% !important;
  }
  .w-50-mobile {
    width: 50% !important;
  }
  .filter-search-input {
    width: 100% !important;
  }
}
</style>
